import { createContext, useContext } from 'react';
import { AppError } from '@mews-framework/errors';
import { SAFE_CONTEXT_INITIAL_VALUE, SafeContextInitialValue } from './constants';
import { createConsumerWithSafeGuard, returnContextValueOrThrow } from './utils';

/** @deprecated use createSafeContext to create a context with safe types allowing nullish values */
export const useSafeContext = <C>(context: React.Context<C>): NonNullable<C> => {
    const contextValue = useContext(context);
    if (!contextValue) {
        throw new AppError(`Context ${context.displayName ?? ''} is not initialized`);
    }
    return contextValue;
};

export const createSafeContext = <Name extends `${Capitalize<string>}Context`, Value extends {}>(
    name: Name,
    initialValue: Value | SafeContextInitialValue = SAFE_CONTEXT_INITIAL_VALUE
) => {
    const context = createContext(initialValue);
    context.displayName = name;

    const provider = { [`${name}Provider`]: context.Provider } as {
        [key in `${Name}Provider`]: typeof context.Provider;
    };

    const ConsumerWithSageGuard = createConsumerWithSafeGuard(context);

    const consumer = { [`${name}Consumer`]: ConsumerWithSageGuard } as {
        [key in `${Name}Consumer`]: typeof ConsumerWithSageGuard;
    };

    const useContextValue = () => {
        const contextValue = useContext(context);
        return returnContextValueOrThrow(contextValue, context.displayName);
    };

    const hook = { [`use${name}`]: useContextValue } as {
        [key in `use${Name}`]: typeof useContextValue;
    };

    return {
        ...provider,
        ...consumer,
        ...hook,
        /**
         * @deprecated
         * used for access in legacy context through static contextType property */
        rawContext: context,
    };
};
